import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import NombreArtista from "../../../components/NombreArtista";
import countries from "../../../utils/countries";
import {
  cuentaRegresiva,
  diasHasta,
  formatearFecha,
  horasRestantes,
  plazoEnPalabras,
} from "../../../utils/fechasEvento";
import { hrefSeguro } from "../../../utils/hrefSeguro";
import { useUser } from "../../../hooks/useUser";
import { planDeEnvio } from "../../../utils/plan";

// Un evento pendiente, tal como lo ve el difusor.
//
// No es una canción con otra carátula: no hay nada que escuchar y lo que
// decide si lo cubre o no son las fechas. Por eso van arriba y destacadas —
// cuándo es, cuándo conviene publicar y cuánto le queda para responder— en
// vez de perdidas entre los datos.
export default function EventoPendiente({ mainData, loadingApi, updateSend }) {
  const { user } = useUser();
  const plan = planDeEnvio(mainData.radarCoin, user?.profile?.tipo);
  const dias = diasHasta(mainData.fecha_inicio);
  const horas = horasRestantes(mainData.fecha, mainData.horas_limite);

  // Un evento cercano se marca para que no se pierda entre los envíos de
  // canciones, que no tienen esta urgencia.
  const urgente = dias !== null && dias <= 7;
  const vencido = horas !== null && horas <= 0;

  const lugar = [mainData.lugar, mainData.ciudad, countries[mainData.pais]]
    .filter(Boolean)
    .join(" · ");

  const cargando = loadingApi.includes(
    `put__productor/envios/${mainData.envioId}`
  );

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
          <Box
            component="img"
            src={mainData.url_img}
            alt={mainData.nombre}
            sx={{
              width: { xs: "100%", sm: 160 },
              maxHeight: 220,
              objectFit: "contain",
              borderRadius: 1,
              bgcolor: "#f0f1f7",
              alignSelf: "flex-start",
            }}
          />

          <Stack flex={1} minWidth={0} gap={1}>
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
              <Chip label={mainData.tipo || "Evento"} size="small" color="primary" />
              {mainData.direct_service_name && (
                <Chip
                  label={mainData.direct_service_name}
                  size="small"
                  variant="outlined"
                  color="primary"
                />
              )}
              {urgente && !vencido && (
                <Chip label="Fecha cercana" size="small" color="warning" />
              )}
              <Typography variant="caption" color="text.secondary">
                {mainData.direct_service_name || plan?.nombre} ·{" "}
                {mainData.radarCoin} RC · te corresponden {mainData.amount} USD
              </Typography>
            </Stack>

            <Typography variant="h6" fontWeight={700}>
              {mainData.nombre}
            </Typography>

            <NombreArtista
              artista={mainData.artista}
              fallback={mainData.profile?.nombre_media}
              variant="subtitle1"
            />

            {/* Las tres fechas, que es lo que define si le sirve cubrirlo. */}
            <Stack
              gap={0.5}
              mt={1}
              p={1.5}
              borderRadius={1}
              bgcolor="#f0f1f7"
            >
              <Typography variant="body2">
                <strong>Fecha del evento:</strong>{" "}
                {formatearFecha(mainData.fecha_inicio)}
                {mainData.fecha_fin
                  ? ` — ${formatearFecha(mainData.fecha_fin)}`
                  : ""}{" "}
                <Typography component="span" variant="body2" color="text.secondary">
                  ({cuentaRegresiva(mainData.fecha_inicio)})
                </Typography>
              </Typography>

              {mainData.fecha_recomendada && (
                <Typography variant="body2">
                  <strong>Se recomienda publicar antes del:</strong>{" "}
                  {formatearFecha(mainData.fecha_recomendada)}
                </Typography>
              )}

              <Typography
                variant="body2"
                color={vencido ? "error" : urgente ? "warning.main" : "text.secondary"}
              >
                <strong>Para responder te queda:</strong>{" "}
                {plazoEnPalabras(mainData.fecha, mainData.horas_limite)}
              </Typography>
            </Stack>

            {vencido && (
              <Alert severity="error" sx={{ mt: 1 }}>
                El plazo de este envío venció; se va a rechazar solo.
              </Alert>
            )}

            {lugar && (
              <Stack direction="row" gap={0.5} alignItems="center" mt={1}>
                <PlaceOutlinedIcon fontSize="small" color="action" />
                <Typography variant="body2">{lugar}</Typography>
              </Stack>
            )}

            {mainData.descripcion && (
              <Typography variant="body2" whiteSpace="pre-line" mt={1}>
                {mainData.descripcion}
              </Typography>
            )}

            <Stack direction="row" gap={1} mt={1} flexWrap="wrap">
              {mainData.url_entradas && (
                <Button
                  size="small"
                  variant="outlined"
                  href={hrefSeguro(mainData.url_entradas)}
                  target="_blank"
                  rel="noreferrer"
                >
                  Ver entradas
                </Button>
              )}
              {/* El material de prensa del artista, que es lo que necesita
                  para escribir la nota. */}
              {mainData.presskit && (
                <Button
                  size="small"
                  variant="outlined"
                  href={mainData.presskit}
                  target="_blank"
                  rel="noreferrer"
                >
                  Descargar presskit
                </Button>
              )}
            </Stack>
          </Stack>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" gap={1} justifyContent="flex-end" flexWrap="wrap">
          <LoadingButton
            loading={cargando}
            color="success"
            startIcon={<CheckCircleOutlineOutlinedIcon />}
            sx={{ textTransform: "none" }}
            onClick={() =>
              updateSend(
                "approve",
                mainData.envioId,
                mainData.url_entradas,
                {
                  cover: mainData.url_img,
                  cancion: mainData.nombre,
                  artista:
                    mainData.artista?.nombre || mainData.profile?.nombre_media,
                  // Para mencionar al artista en el texto de la publicación.
                  redes: mainData.artista?.redes,
                  // Un envío de 5 RC exige un link de un canal aprobado.
                  radarCoin: mainData.radarCoin,
                  // Cuándo y dónde, para el texto que acompaña al flyer.
                  detalle: [formatearFecha(mainData.fecha_inicio), lugar]
                    .filter(Boolean)
                    .join(" · "),
                },
                "evento"
              )
            }
          >
            Aprobar
          </LoadingButton>
          <LoadingButton
            loading={cargando}
            color="error"
            startIcon={<HighlightOffOutlinedIcon />}
            sx={{ textTransform: "none" }}
            onClick={() =>
              updateSend(
                "rejected",
                mainData.envioId,
                mainData.url_entradas,
                undefined,
                "evento"
              )
            }
          >
            Rechazar
          </LoadingButton>
        </Stack>
      </CardContent>
    </Card>
  );
}
